import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { AUTH_ROUTES } from '../../authConstants';
import { usePermissions } from '../../../permissions/usePermissions';
import type { LineId, PermissionAction } from '../../../permissions/permissionTypes';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Optional permission gate (HOMCO-640). When set, the route also requires
   * `action` (default `display`) on `line`; while the rights are still loading a
   * loader is shown, and a missing right redirects to `/403`.
   */
  permission?: { line: LineId; action?: PermissionAction };
}

function Loader() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
}

/**
 * Wrapping déclaratif pour les routes nécessitant une authentification (et,
 * optionnellement, une permission — HOMCO-640).
 *
 * Usage dans router.tsx :
 *   <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
 *   <Route element={<ProtectedRoute permission={{ line: 'employee_list' }}>…</ProtectedRoute>} />
 */
export function ProtectedRoute({ children, permission }: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized } = useAuth();
  const { can } = usePermissions();

  // The loading window is `!isInitialized`: the rights are fetched (GET /auth/me)
  // as part of the auth bootstrap, so once initialized they have SETTLED — whether
  // the fetch succeeded or failed. A failed /auth/me leaves permissions null, which
  // `can()` already treats as "nothing granted". We must therefore evaluate `can()`
  // here rather than wait on an `isLoaded` flag that stays false after a failure —
  // otherwise a failed /auth/me (permissions stuck null) would spin a gated route
  // forever instead of failing closed to /403.
  if (!isInitialized) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to={AUTH_ROUTES.LOGIN} replace />;
  }

  if (permission && !can(permission.line, permission.action ?? 'display')) {
    // Missing right OR rights that failed to load → fail closed to /403.
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}
